<script setup>
import { cn } from "@/lib/utils";
import { computed } from "vue";
import { useAttrs } from "vue";
import BeatLoader from "vue-spinner/src/BeatLoader.vue";
import buttonVariants from "./variants";

const attrs = useAttrs();

const props = defineProps({
  as: {
    type: String,
    default: "button",
  },
  variant: {
    type: String,
    default: "default",
  },
  size: {
    type: String,
    default: "default",
  },
  type: {
    type: String,
    default: "button",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  dataTestid: {
    type: [String, undefined],
    default: undefined,
  },
});

const { as, variant, size } = props;
const element = computed(() =>
  attrs?.onClick || props.type === "submit" ? as : "span"
);

const color = computed(() => {
  switch (variant) {
    case "outline-primary":
      return "var(--primary-500)";
    case "outline-danger":
      return "var(--danger-500)";
    case "outline-success":
      return "var(--success-500)";
    case "outline-info":
      return "var(--info-500)";
    case "outline-warning":
      return "var(--warning-500)";
    case "outline-muted":
      return "var(--neutral-200)";
    default:
      return "var(--neutral-50)";
  }
});

const VALID_VARIANTS = new Set([
  "primary",
  "danger",
  "success",
  "info",
  "warning",
  "muted",
  "outline-primary",
  "outline-danger",
  "outline-success",
  "outline-info",
  "outline-warning",
  "outline-muted",
  "outline-transparent",
  "ghost",
]);

const VALID_SIZES = new Set(["default", "sm", "lg", "icon", "xs"]);

const safeVariant = computed(() => {
  return VALID_VARIANTS.has(variant) ? variant : "muted";
});

const safeSize = computed(() => {
  return VALID_SIZES.has(size) ? size : "default";
});
</script>

<template>
  <component
    :is="element"
    :disabled="loading"
    :class="
      cn(
        buttonVariants({ variant: safeVariant, size: safeSize }),
        $attrs.class ?? '',
        't'
      )
    "
    :data-testid="dataTestid"
  >
    <BeatLoader
      v-if="loading"
      :color="color"
      size="8px"
      class="vf-translate-y-0.5"
    />
    <slot v-else />
  </component>
</template>
